import React from 'react';
import { observer } from 'mobx-react';

import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';

import TabPanel from 'components/CustomTabPanel/CustomTabPanel';

import { colors } from 'theme/colors';

import ActionsTab from './tabs/ActionsTab';
import ActivityTab from './tabs/ActivityTab';
import LinkedItemsTab from './tabs/LinkedItemsTab';
import { AuditQuestionDrawerProps, AuditQuestionDrawerTab } from './types';
import { DrawerBody, HeaderContainer, QuestionHeader, StyledTab, StyledTabs } from './styles';

// The only flex child that grows; `minHeight: 0` lets the scroll area shrink below content height.
const ACTIVE_PANEL_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

/** Shared by fill-out and the audit record — the host lives on the store, so nothing here forks. */
const AuditQuestionDrawer = ({
  activities,
  questionKey,
  questionLabel,
  tab,
  onTabChange,
  onClose,
  lookupRelations = [],
  lookupModule,
  readOnlyLinks = false,
  entityUuid,
}: AuditQuestionDrawerProps): React.ReactElement => {
  React.useEffect(() => {
    // The tabs switch the question themselves (ensureFeed/ensureLinks) because their effects run
    // first; this covers the question having no tab mounted yet.
    activities.setQuestion(questionKey);
    // An empty map is normal for an audit with no artefacts, so it cannot stand in for "not loaded".
    if (!activities.statsLoaded) {
      activities.loadStats();
    }
  }, [activities, questionKey]);

  return (
    <DrawerBody data-testid="audit-question-drawer">
      <HeaderContainer>
        {/* Falls back like the record row; `title` recovers a label the two-line clamp cuts. */}
        <QuestionHeader title={questionLabel || undefined}>{questionLabel || '-'}</QuestionHeader>
        <IconButton
          onClick={onClose}
          aria-label="Close question panel"
          sx={{
            borderRadius: '50%',
            border: `1px solid ${colors.grey[300]}`,
            width: '32px',
            height: '32px',
          }}
        >
          <Close sx={{ width: '18px', height: '18px' }} />
        </IconButton>
      </HeaderContainer>
      {/* Tasks leads, like the row buttons that open it; explicit values keep the `?tab=` numbers as they were. */}
      <StyledTabs value={tab} onChange={(_, newTab: number) => onTabChange(newTab as AuditQuestionDrawerTab)}>
        <StyledTab label="Tasks" value={AuditQuestionDrawerTab.Tasks} data-testid="audit-question-drawer-tab-actions" />
        <StyledTab
          label="Activity feed"
          value={AuditQuestionDrawerTab.Activity}
          data-testid="audit-question-drawer-tab-activity"
        />
        <StyledTab
          label="Related records"
          value={AuditQuestionDrawerTab.LinkedItems}
          data-testid="audit-question-drawer-tab-linked-items"
        />
      </StyledTabs>
      <TabPanel value={tab} index={AuditQuestionDrawerTab.Tasks} customStyle={ACTIVE_PANEL_STYLE}>
        <ActionsTab activities={activities} questionKey={questionKey} entityUuid={entityUuid} />
      </TabPanel>
      <TabPanel value={tab} index={AuditQuestionDrawerTab.Activity} customStyle={ACTIVE_PANEL_STYLE}>
        <ActivityTab activities={activities} questionKey={questionKey} />
      </TabPanel>
      <TabPanel value={tab} index={AuditQuestionDrawerTab.LinkedItems} customStyle={ACTIVE_PANEL_STYLE}>
        <LinkedItemsTab
          activities={activities}
          questionKey={questionKey}
          lookupRelations={lookupRelations}
          lookupModule={lookupModule}
          readOnly={readOnlyLinks}
        />
      </TabPanel>
    </DrawerBody>
  );
};

export default observer(AuditQuestionDrawer);
